import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import CreateNewSuccessCriteriaDesktop from './CreateNewSuccessCriteriaDesktop';
import Toast from '../../Toast';
import { useSuccessCriteria } from '../../state/hooks';
import { createSuccessCriteria } from '../../../service';
import useNavigationBlock from '../../../hooks/useNavigationBlock';
import GeneralPopup from '../../GeneralPopup';
import { getUserId } from '../../../userLocalDetails';
import { useProfile } from '../../state/hooks';
import { useUserStates } from '../../../hooks/useUserStates';
import { buildMetadata } from '../../../utils/subjects';
import { useSubjects } from '../../../hooks/useSubjects';

const getDefaultSuccessCriteria = () => ({
  title: 'Untitled Success Criteria',
  criterias: [
    {
      objectives: [
        { description: 'Begins each introduction with a clear topic' },
        { description: 'Develops topic sentences in a logical, conceptual progression so each paragraph advances the argument' },
        { description: 'Organises each paragraph so idea flow clearly' },
      ],
      selectedStatus: null,
    },
  ],
  years: [],
  subjects: [],
  taskTypes: [],
  responseTypes: [],
  ownerId: getUserId(),
});

export default function CreateNewSuccessCriteriaRoot() {
  const {
    setIsNavigationBlocked,
    isShowNavigationBlockPopup,
    confirmNavigationChange,
    cancelNavigationPopup,
  } = useNavigationBlock();

  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const { rawSubjects: subjectsList } = useSubjects(userStates);
  const [successCriteria, setSuccessCriteria] = useState(getDefaultSuccessCriteria);

  const { resetData: resetSuccessCriteria } = useSuccessCriteria();

  const handleTitleChange = (event) => {
    setIsNavigationBlocked(true);
    setSuccessCriteria({ ...successCriteria, title: event.target.value });
  };

  const handleObjectiveChange = (index, description) => {
    setIsNavigationBlocked(true);
    const newCriterias = [...successCriteria.criterias];
    newCriterias[0].objectives[index] = { description };
    setSuccessCriteria({ ...successCriteria, criterias: newCriterias });
  };

  const handleDeleteObjective = (index) => {
    setIsNavigationBlocked(true);
    const newCriterias = [...successCriteria.criterias];
    newCriterias[0].objectives.splice(index, 1);
    setSuccessCriteria({ ...successCriteria, criterias: newCriterias });
  };

  const handleAddObjective = () => {
    setIsNavigationBlocked(true);
    const newCriterias = [...successCriteria.criterias];
    newCriterias[0].objectives.push({ description: '' });
    setSuccessCriteria({ ...successCriteria, criterias: newCriterias });
  };

  const updateSuccessCriteria = (updates) => {
    setIsNavigationBlocked(true);
    setSuccessCriteria({ ...successCriteria, ...updates });
  };

  // Helper to convert subjects to object format with {subjectCode, subjectName}
  const subjectsAsObjects = (subjects) => {
    if (!Array.isArray(subjects)) return [];
    return subjects.map((s) => {
      // If already in correct format
      if (typeof s === 'object' && s !== null && s.subjectCode && s.subjectName) {
        return s;
      }
      // If object but missing fields
      if (typeof s === 'object' && s !== null) {
        return {
          subjectCode: s.subjectCode || s.code || s,
          subjectName: s.subjectName || s.name || s.title || s
        };
      }
      // If string, look up in subjectsList
      const subjectObj = subjectsList.find((sub) =>
        (sub.subjectCode || sub.code) === s || (sub.subjectName || sub.name || sub.title) === s
      );
      if (subjectObj) {
        return {
          subjectCode: subjectObj.subjectCode || subjectObj.code || s,
          subjectName: subjectObj.subjectName || subjectObj.name || subjectObj.title || s
        };
      }
      return null;
    }).filter(Boolean);
  };

  const saveSuccessCriteria = (settings = {}) => {
    const years = settings.years || successCriteria.years || [];
    const rawSubjects = settings.subjects || successCriteria.subjects || [];
    const taskTypes = settings.taskTypes || successCriteria.taskTypes || (successCriteria.taskType ? [successCriteria.taskType] : []);
    const responseTypes = settings.responseTypes || successCriteria.responseTypes || (successCriteria.responseType ? [successCriteria.responseType] : []);
    const metadata = buildMetadata(userStates, { years, subjects: rawSubjects, taskTypes, responseTypes }, subjectsList);

    // Convert subjects to object format for API
    const subjectsForApi = subjectsAsObjects(rawSubjects);

    const newSuccessCriteria = {
      title: successCriteria.title,
      criterias: successCriteria.criterias,
      years,
      taskTypes,
      responseTypes,
      metadata,
    };

    if (years.length === 0 || rawSubjects.length === 0) {
      toast(<Toast message="Please select at least one Year and one Subject" />);
      return;
    }
    if (!newSuccessCriteria.title.trim()) {
      toast(<Toast message="Please add a title for your success criteria" />);
      return;
    }

    const objectives = newSuccessCriteria.criterias[0]?.objectives || [];
    if (objectives.length === 0) {
      toast(<Toast message="Please add at least one objective" />);
      return;
    }

    createSuccessCriteria(newSuccessCriteria)
      .then((response) => {
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
        resetSuccessCriteria();

        setIsNavigationBlocked(false);

        toast(<Toast message="Success criteria created" />);

        const selfLink = response?._links?.self?.href || response?.id;
        if (selfLink) {
          const newId = typeof selfLink === 'string' && selfLink.includes('/')
            ? selfLink.split('/').pop()
            : selfLink;
          history.push(`/tools/successcriterias/${newId}`);
        } else {
          history.push('/tools/successcriterias');
        }
      })
      .catch((error) => {
        console.error('Error saving success criteria:', error);
        toast(<Toast message="Error saving success criteria" />);
      });
  };

  return (
    <>
      {isShowNavigationBlockPopup && (
        <GeneralPopup
          hidePopup={cancelNavigationPopup}
          title="Unsaved Changes"
          textContent="You have unsaved changes in your success criteria. If you leave this page, your changes will be lost. Do you want to continue?"
          buttonText="Leave Without Saving"
          confirmButtonAction={confirmNavigationChange}
          closeBtnText="Stay on Page"
          cancelPopup={cancelNavigationPopup}
        />
      )}
      <CreateNewSuccessCriteriaDesktop
        successCriteria={successCriteria}
        updateSuccessCriteria={updateSuccessCriteria}
        handleTitleChange={handleTitleChange}
        handleObjectiveChange={handleObjectiveChange}
        handleDeleteObjective={handleDeleteObjective}
        handleAddObjective={handleAddObjective}
        saveSuccessCriteria={saveSuccessCriteria}
        isUpdating={false}
        successCriteriaId="new"
      />
    </>
  );
}
